import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * GM inbox: VPPL's pending edit-requests on job cards.
 *
 * Approving a request writes an approved token that VPPL then consumes
 * ONCE by editing the job card and saving.  Rejecting closes the request
 * with an optional remark.
 *
 * A history tab lists every request in every state so a decision that
 * was already made is easy to look back on.
 */
interface Row {
  requestId: number;
  jobId: string;
  jobName: string | null;
  clientName: string | null;
  requestedBy: string | null;
  reason: string;
  requestedOn: string;
  status: 'Pending' | 'Approved' | 'Rejected' | 'Consumed' | 'Direct';
  decidedBy: string | null;
  decidedOn: string | null;
  decisionRemark: string | null;
  consumedOn: string | null;
}

type Tab = 'Pending' | 'All';

export function EditRequestsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('Pending');
  const [rowRemark, setRowRemark] = useState<Record<number, string>>({});

  const list = useQuery({
    queryKey: ['job-edit-requests', tab],
    queryFn: () => api<{ rows: Row[] }>(
      `/api/job-orders/edit-requests?status=${tab === 'All' ? 'all' : 'Pending'}`,
    ),
  });

  const decide = useMutation({
    mutationFn: (v: { id: number; action: 'Approved' | 'Rejected'; remark?: string }) =>
      api(`/api/job-orders/edit-requests/${v.id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ action: v.action, remark: v.remark }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job-edit-requests'] }),
  });

  const rows = list.data?.rows ?? [];
  const pendingCount = tab === 'Pending' ? rows.length : rows.filter(r => r.status === 'Pending').length;

  return (
    <div className="p-6">
      <h1 className="text-xl font-semibold text-brand mb-1">Job Card Edit Requests</h1>
      <p className="text-sm text-slate-500 mb-4">
        VPPL raises a request with a reason; approve to grant a one-time edit token,
        or reject with a remark.  The token is consumed when VPPL saves the edit —
        after that they must raise a new request.
      </p>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {(['Pending', 'All'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${
                    tab === t
                      ? 'border-brand text-brand'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}>
            {t === 'Pending' ? `Pending (${pendingCount})` : 'History'}
          </button>
        ))}
      </div>

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Job / Client</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Requested by</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Reason</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">When</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Status</th>
              <th className="px-3 py-2 text-right font-medium text-slate-600">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.requestId} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 align-top">
                  <Link to={`/job-orders/${encodeURIComponent(r.jobId)}/print`}
                        className="font-mono text-brand hover:underline">
                    {r.jobId.split('/').pop()}
                  </Link>
                  <div className="text-slate-600">{r.jobName || '—'}</div>
                  <div className="text-slate-400 text-[10px]">{r.clientName || '—'}</div>
                </td>
                <td className="px-3 py-2 align-top">{r.requestedBy || '—'}</td>
                <td className="px-3 py-2 align-top max-w-[420px] whitespace-pre-wrap">{r.reason}</td>
                <td className="px-3 py-2 align-top text-slate-500">{r.requestedOn}</td>
                <td className="px-3 py-2 align-top">
                  <span className={
                    r.status === 'Pending'  ? 'text-amber-700'    :
                    r.status === 'Approved' ? 'text-emerald-700'  :
                    r.status === 'Rejected' ? 'text-red-700'      :
                    r.status === 'Direct'   ? 'text-blue-700'     :
                                              'text-slate-500'
                  }>{r.status === 'Direct' ? 'Direct edit' : r.status}</span>
                  {r.status === 'Direct' && (
                    <div className="text-slate-400 text-[10px] mt-0.5">one of the month's direct edits - no approval</div>
                  )}
                  {r.decidedBy && (
                    <div className="text-slate-400 text-[10px] mt-0.5">
                      {r.decidedBy}, {r.decidedOn}
                      {r.decisionRemark && <div className="italic">"{r.decisionRemark}"</div>}
                    </div>
                  )}
                  {r.consumedOn && r.status !== 'Direct' && (
                    <div className="text-slate-400 text-[10px] mt-0.5">
                      Consumed {r.consumedOn}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 align-top text-right">
                  {r.status === 'Pending' ? (
                    <div className="flex flex-col items-end gap-1">
                      <input className="input py-0.5 text-xs w-52" placeholder="Remark (optional)…"
                             value={rowRemark[r.requestId] ?? ''}
                             onChange={e => setRowRemark({ ...rowRemark, [r.requestId]: e.target.value })} />
                      <div className="flex gap-2">
                        <button className="text-emerald-700 hover:underline text-xs"
                                disabled={decide.isPending}
                                onClick={() => decide.mutate({ id: r.requestId, action: 'Approved', remark: rowRemark[r.requestId] })}>
                          Approve
                        </button>
                        <button className="text-red-700 hover:underline text-xs"
                                disabled={decide.isPending}
                                onClick={() => decide.mutate({ id: r.requestId, action: 'Rejected', remark: rowRemark[r.requestId] })}>
                          Reject
                        </button>
                      </div>
                    </div>
                  ) : <span className="text-slate-400">—</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400">
                {tab === 'Pending' ? 'No pending requests.' : 'No history yet.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
